import React from 'react';
import { CardTW } from 'tailwindComponents/CardTW/CardTW';
import { CardFlexRowTW } from './feesOverviewCards/feesOverviewCardsStylesTW';
import { Dropdown } from 'tailwindComponents/dropdown/Dropdown';
import {
  useGetFpeStatusesDataQuery,
  useUpdateBalancingClosingMutation,
  useUpdateDisbursementMutation,
  useUpdateFundingAuthorizationMutation,
  useUpdatePreliminaryFeesMutation,
} from 'API/order/fpeStatus/FpeStatusAPI';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { PreliminaryFeesSent, PreliminaryFeesSentValues } from 'types/order/fpeStatuses/ePreliminaryFeesSent';
import { FundingAuthorization, FundingAuthorizationValues } from 'types/order/fpeStatuses/eFundingAuthorization';
import { BalancingClosing, BalancingClosingValues } from 'types/order/fpeStatuses/eBalancingClosing';
import { Disbursement, DisbursementValues } from 'types/order/fpeStatuses/eDisbursement';
import { FundingCompleteContainer } from './FundingCompleteContainer';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';

interface IFundingStatusesProps {
  orderId: string;
}

export const FundingStatuses: React.FC<IFundingStatusesProps> = ({ orderId }) => {
  const { data: statuses, isLoading, isSuccess } = useGetFpeStatusesDataQuery(orderId);
  const [updatePreliminaryFees, { isLoading: isLoadingPreliminaryFees }] = useUpdatePreliminaryFeesMutation();
  const [updateBalancingClosing, { isLoading: isLoadingBalancingClosing }] = useUpdateBalancingClosingMutation();
  const [updateFundingAuthorization, { isLoading: isLoadingundingAuthorization }] =
    useUpdateFundingAuthorizationMutation();
  const [updateDisbursement, { isLoading: isLoadingDisbursement }] = useUpdateDisbursementMutation();

  const onUpdatePreliminaryFees = async (statusValue: PreliminaryFeesSent) => {
    checkForMutationSuccess(
      await updatePreliminaryFees({
        orderId,
        statusValue,
      }),
      'updating preliminary fees status'
    );
  };

  const onUpdateBalancingClosing = async (statusValue: BalancingClosing) => {
    checkForMutationSuccess(
      await updateBalancingClosing({
        orderId,
        statusValue,
      }),
      'updating balancing status'
    );
  };

  const onUpdateFundingAuthorization = async (statusValue: FundingAuthorization) => {
    checkForMutationSuccess(
      await updateFundingAuthorization({
        orderId,
        statusValue,
      }),
      'updating funding authorization'
    );
  };

  const onUpdateDisbursement = async (statusValue: Disbursement) => {
    checkForMutationSuccess(
      await updateDisbursement({
        orderId,
        statusValue,
      }),
      'updating disbursment status'
    );
  };

  return (
    <CardTW contentClassName="p-5">
      {isSuccess && statuses && (
        <CardFlexRowTW>
          <Dropdown
            title="Preliminary Fees"
            options={PreliminaryFeesSentValues}
            option={statuses.preliminaryFeesSent.value || 'No'}
            onSelect={onUpdatePreliminaryFees}
            style={matchStyle(statuses.preliminaryFeesSent?.value)}
            isLoading={isLoadingPreliminaryFees}
            tooltipText={getTooltip(statuses.preliminaryFeesSent.updatedBy, statuses.preliminaryFeesSent.updatedAt)}
          />
          <Dropdown
            title="Balancing"
            options={BalancingClosingValues}
            option={statuses.balancingClosing.value || 'Not Started'}
            onSelect={onUpdateBalancingClosing}
            style={matchStyle(statuses.balancingClosing.value)}
            isLoading={isLoadingBalancingClosing}
            tooltipText={getTooltip(statuses.balancingClosing.updatedBy, statuses.balancingClosing.updatedAt)}
          />
          <Dropdown
            title="Funding Authorization"
            options={FundingAuthorizationValues}
            option={statuses.fundingAuthorization.value || 'Funding Not Authorized'}
            onSelect={onUpdateFundingAuthorization}
            style={matchStyle(statuses.fundingAuthorization.value)}
            isLoading={isLoadingundingAuthorization}
            tooltipText={getTooltip(statuses.fundingAuthorization.updatedBy, statuses.fundingAuthorization.updatedAt)}
          />
          <Dropdown
            title="Disbursement"
            options={DisbursementValues}
            option={statuses.disbursement.value || 'Disbursement Setup Pending'}
            onSelect={onUpdateDisbursement}
            style={matchStyle(statuses.disbursement.value)}
            isLoading={isLoadingDisbursement}
            tooltipText={getTooltip(statuses.disbursement.updatedBy, statuses.disbursement.updatedAt)}
          />
          <div className="flex flex-row grow justify-end items-center">
            <FundingCompleteContainer />
          </div>
        </CardFlexRowTW>
      )}
      {isLoading && <SpinnerTW />}
    </CardTW>
  );
};

const getTooltip = (updatedBy: string, updatedAt: string) =>
  updatedBy
    ? `Updated by ${updatedBy} ${new Date(updatedAt).toLocaleDateString()} ${new Date(updatedAt).toLocaleTimeString()}`
    : 'Not changed yet';

const matchStyle = (value: string) => {
  switch (value) {
    case 'Yes':
    case 'Complete':
    case 'Funding Authorized':
    case 'Disbursement Setup Complete':
      return 'complete';
    case 'In Progress':
      return 'progress';
    case 'N/A':
    case 'Not Applicable':
      return 'error';
    default:
      return 'default';
  }
};
